import type { TakiSdk } from '@taki/sdk';

/**
 * LE JEU DE DEMONSTRATION de l'app Catalogue : ce que vend une clinique
 * veterinaire belge, des vaccins aux actes, avec le prix hors taxe, la TVA et
 * le prix TTC (calcule, jamais saisi : `ttc`), cinq protocoles et leurs lignes,
 * deux calculs de dose et leurs posologies.
 *
 * Il se pose EN PREMIER : les demos de Patients et de Consultations retrouvent
 * ces produits par leur `demoKey`. Les clefs `pa-r*` et `ca-*` sont celles de la
 * demo de l'app Consultations d'origine ; les noms de produits sont courants,
 * les prix sont inventes.
 *
 * LES VACCINS ET LES ANTIPARASITAIRES portent leur prevention (ce qu'ils
 * couvrent, les especes, le delai de rappel) : le carnet de vaccination de
 * Patients couvre ainsi les plans du chien, du chat, du lapin et du cheval.
 * LES MEDICAMENTS D'ANESTHESIE portent leur concentration (mg par unite) : le
 * calculateur de dose de Consultations la lit.
 *
 * LES POSOLOGIES sont reprises de sources publiques, citees dans chaque fiche
 * (Universite de Bristol, Improve International) : elles montrent le calcul, la
 * clinique pose les siennes.
 *
 * Memes regles que `../taches/demo-dataset.ts` : tout passe par `@taki/sdk`,
 * IDEMPOTENT par `customFields.demoKey`, aucune suppression, valeurs STOCKEES
 * des catalogues (jamais les libelles).
 */

const TYPE_ARTICLE = 'article';
const TYPE_PROTOCOLE = 'protocole';
const TYPE_LIGNE = 'ligne';

/** Ce que l'import a fait, rendu au hub Parametres. */
export interface DemoImportResult {
  created: number;
  skipped: number;
  message: string;
}

/** Les taux du catalogue, tels que le natif `taxRate` les stocke : une fraction. */
const TVA = { six: 0.06, vingtEtUn: 0.21 } as const;
type Tva = (typeof TVA)[keyof typeof TVA];

export interface DemoProduit {
  demoKey: string;
  name: string;
  category: 'Vaccin' | 'Médicament' | 'Consommable' | 'Équipement' | 'Acte';
  unit: string;
  sku: string;
  /** Prix hors taxe. Le TTC se calcule : `ttc`. */
  basePrice: number;
  taxRate: Tva;
  /** La prevention : ce que le produit couvre, pour quelles especes, et son delai de rappel (carnet de vaccination). */
  prevention?: { couvre: readonly string[]; especes: readonly string[]; rappel: string };
  /** Les mg par unite du produit (par ml, par comprime) : le calculateur de dose de Consultations la lit. */
  concentration?: number;
}

/** Le prix TTC : le HT majore de la TVA, arrondi au centime. */
export function ttc(basePrice: number, taxRate: number): number {
  return Math.round(basePrice * (1 + taxRate) * 100) / 100;
}

const v = (
  demoKey: string, name: string, sku: string, basePrice: number,
  couvre: string, especes: readonly string[], rappel: string,
): DemoProduit => ({
  demoKey, name, category: 'Vaccin', unit: 'dose', sku, basePrice, taxRate: TVA.six,
  prevention: { couvre: [couvre], especes, rappel },
});

export const DEMO_PRODUITS: ReadonlyArray<DemoProduit> = [
  /* LES VACCINS : un par maladie du plan de chaque espece. */
  v('pa-r1', 'Nobivac DHPPi', 'VAC-001', 26.42, 'CHPPi', ['Chien'], '1 an'),
  v('pa-r2', 'Nobivac Rabies', 'VAC-002', 21.7, 'Rage', ['Chien', 'Chat'], '1 an'),
  v('pa-r3', 'Nobivac Tricat Trio', 'VAC-003', 24.53, 'Typhus-Coryza', ['Chat'], '1 an'),
  v('pa-r4', 'Nobivac Myxo-RHD', 'VAC-004', 19.81, 'Myxomatose-VHD', ['Lapin'], '1 an'),
  v('pa-r5', 'Equilis Te', 'VAC-005', 28.3, 'Tétanos', ['Cheval'], '1 an'),
  v('ca-v6', 'Versican Plus L4', 'VAC-006', 23.58, 'Leptospirose', ['Chien'], '1 an'),
  v('ca-v7', 'Nobivac KC', 'VAC-007', 22.64, 'Toux de chenil', ['Chien'], '1 an'),
  v('ca-v8', 'Purevax FeLV', 'VAC-008', 25.47, 'Leucose', ['Chat'], '1 an'),
  v('ca-v9', 'Equilis Prequenza', 'VAC-009', 33.02, 'Grippe équine', ['Cheval'], '6 mois'),
  /* LES ANTIPARASITAIRES : vermifuges et puces-tiques, rappel de 1 a 6 mois. */
  { demoKey: 'pa-r6', name: 'Milbemax', category: 'Médicament', unit: 'comprimé', sku: 'MED-001', basePrice: 7.55, taxRate: TVA.six, prevention: { couvre: ['Vermifuge'], especes: ['Chien', 'Chat'], rappel: '3 mois' } },
  { demoKey: 'pa-r7', name: 'Vermifuge équin', category: 'Médicament', unit: 'seringue', sku: 'MED-002', basePrice: 13.3, taxRate: TVA.six, prevention: { couvre: ['Vermifuge'], especes: ['Cheval'], rappel: '6 mois' } },
  { demoKey: 'ca-m6', name: 'Bravecto', category: 'Médicament', unit: 'comprimé', sku: 'MED-006', basePrice: 31.13, taxRate: TVA.six, prevention: { couvre: ['Puces et tiques'], especes: ['Chien', 'Chat'], rappel: '3 mois' } },
  { demoKey: 'ca-m19', name: 'Panacur lapin', category: 'Médicament', unit: 'seringue', sku: 'MED-019', basePrice: 6.6, taxRate: TVA.six, prevention: { couvre: ['Vermifuge'], especes: ['Lapin'], rappel: '3 mois' } },
  { demoKey: 'ca-m20', name: 'Advocate chat', category: 'Médicament', unit: 'pipette', sku: 'MED-020', basePrice: 11.32, taxRate: TVA.six, prevention: { couvre: ['Puces et tiques'], especes: ['Chat'], rappel: '1 mois' } },
  /* LES MEDICAMENTS : l'unite est celle dans laquelle on administre et on facture, la concentration est en mg par unite. */
  { demoKey: 'ca-m3', name: 'Metacam 1,5 mg/ml', category: 'Médicament', unit: 'ml', sku: 'MED-003', basePrice: 0.85, taxRate: TVA.six, concentration: 1.5 },
  { demoKey: 'ca-m4', name: 'Synulox 250 mg', category: 'Médicament', unit: 'comprimé', sku: 'MED-004', basePrice: 1.13, taxRate: TVA.six, concentration: 250 },
  { demoKey: 'ca-m5', name: 'Dexdomitor 0,5 mg/ml', category: 'Médicament', unit: 'ml', sku: 'MED-005', basePrice: 4.2, taxRate: TVA.six, concentration: 0.5 },
  /* L'ANESTHESIE, pour le calculateur de dose : les concentrations des deux sources de `DEMO_POSOLOGIES`. */
  { demoKey: 'ca-m7', name: 'Acépromazine 2 mg/ml', category: 'Médicament', unit: 'ml', sku: 'MED-007', basePrice: 0.47, taxRate: TVA.six, concentration: 2 },
  { demoKey: 'ca-m8', name: 'Buprénorphine 0,3 mg/ml', category: 'Médicament', unit: 'ml', sku: 'MED-008', basePrice: 3.77, taxRate: TVA.six, concentration: 0.3 },
  { demoKey: 'ca-m9', name: 'Alfaxalone 10 mg/ml', category: 'Médicament', unit: 'ml', sku: 'MED-009', basePrice: 2.83, taxRate: TVA.six, concentration: 10 },
  { demoKey: 'ca-m10', name: 'Metacam 5 mg/ml injectable', category: 'Médicament', unit: 'ml', sku: 'MED-010', basePrice: 3.3, taxRate: TVA.six, concentration: 5 },
  { demoKey: 'ca-m11', name: 'Metacam 2 mg/ml injectable chat', category: 'Médicament', unit: 'ml', sku: 'MED-011', basePrice: 2.83, taxRate: TVA.six, concentration: 2 },
  { demoKey: 'ca-m12', name: 'Dexmédétomidine 0,1 mg/ml', category: 'Médicament', unit: 'ml', sku: 'MED-012', basePrice: 2.36, taxRate: TVA.six, concentration: 0.1 },
  { demoKey: 'ca-m13', name: 'Méthadone 10 mg/ml', category: 'Médicament', unit: 'ml', sku: 'MED-013', basePrice: 3.77, taxRate: TVA.six, concentration: 10 },
  { demoKey: 'ca-m14', name: 'Propofol 10 mg/ml', category: 'Médicament', unit: 'ml', sku: 'MED-014', basePrice: 1.42, taxRate: TVA.six, concentration: 10 },
  /* LES CONSOMMABLES ET LES EQUIPEMENTS. */
  { demoKey: 'pa-r9', name: 'Seringue stérile', category: 'Consommable', unit: 'pièce', sku: 'CSM-002', basePrice: 0.41, taxRate: TVA.vingtEtUn },
  { demoKey: 'ca-c2', name: 'Pansement cohésif', category: 'Consommable', unit: 'rouleau', sku: 'CSM-003', basePrice: 3.31, taxRate: TVA.vingtEtUn },
  { demoKey: 'ca-c3', name: 'Gel échographique', category: 'Consommable', unit: 'dose', sku: 'CSM-004', basePrice: 1.24, taxRate: TVA.vingtEtUn },
  { demoKey: 'ca-c4', name: 'Compresses stériles', category: 'Consommable', unit: 'sachet', sku: 'CSM-005', basePrice: 2.1, taxRate: TVA.vingtEtUn },
  { demoKey: 'pa-r8', name: 'Collerette', category: 'Équipement', unit: 'pièce', sku: 'EQP-001', basePrice: 9.92, taxRate: TVA.vingtEtUn },
  { demoKey: 'ca-e2', name: 'Laisse', category: 'Équipement', unit: 'pièce', sku: 'EQP-002', basePrice: 12.4, taxRate: TVA.vingtEtUn },
  /* LES ACTES. */
  { demoKey: 'pa-r10', name: 'Consultation', category: 'Acte', unit: 'acte', sku: 'ACT-001', basePrice: 41.32, taxRate: TVA.vingtEtUn },
  { demoKey: 'ca-a2', name: 'Vaccination', category: 'Acte', unit: 'acte', sku: 'ACT-002', basePrice: 16.53, taxRate: TVA.vingtEtUn },
  { demoKey: 'ca-a3', name: 'Castration chat', category: 'Acte', unit: 'acte', sku: 'ACT-003', basePrice: 82.64, taxRate: TVA.vingtEtUn },
  { demoKey: 'ca-a4', name: 'Échographie abdominale', category: 'Acte', unit: 'acte', sku: 'ACT-004', basePrice: 57.85, taxRate: TVA.vingtEtUn },
  { demoKey: 'ca-a5', name: 'Tonte', category: 'Acte', unit: 'acte', sku: 'ACT-005', basePrice: 8.26, taxRate: TVA.vingtEtUn },
  { demoKey: 'ca-a6', name: 'Consultation d’urgence', category: 'Acte', unit: 'acte', sku: 'ACT-006', basePrice: 66.12, taxRate: TVA.vingtEtUn },
  { demoKey: 'ca-a7', name: 'Prise de sang', category: 'Acte', unit: 'acte', sku: 'ACT-007', basePrice: 24.79, taxRate: TVA.vingtEtUn },
  { demoKey: 'ca-a8', name: 'Radiographie', category: 'Acte', unit: 'acte', sku: 'ACT-008', basePrice: 45.45, taxRate: TVA.vingtEtUn },
  { demoKey: 'ca-a10', name: 'Visite à l’écurie', category: 'Acte', unit: 'acte', sku: 'ACT-010', basePrice: 82.64, taxRate: TVA.vingtEtUn },
];

export interface DemoProtocole {
  demoKey: string;
  name: string;
  description: string;
  /** Les lignes : le produit (par `demoKey`) et la quantite. */
  lignes: ReadonlyArray<{ produit: string; quantite: number }>;
}

/** Les protocoles : des produits du jeu ci-dessus (par `demoKey`) qui vont ensemble. */
export const DEMO_PROTOCOLES: ReadonlyArray<DemoProtocole> = [
  {
    demoKey: 'ca-p1',
    name: 'Échographie',
    description: 'Tonte, échographie abdominale, gel, sédation si l’animal ne se laisse pas faire.',
    lignes: [
      { produit: 'ca-a5', quantite: 1 },
      { produit: 'ca-a4', quantite: 1 },
      { produit: 'ca-c3', quantite: 2 },
      { produit: 'ca-m5', quantite: 1 },
    ],
  },
  {
    demoKey: 'ca-p2',
    name: 'Vaccin annuel chien',
    description: 'Acte de vaccination, DHPPi, leptospirose et rage.',
    lignes: [
      { produit: 'ca-a2', quantite: 1 },
      { produit: 'pa-r1', quantite: 1 },
      { produit: 'ca-v6', quantite: 1 },
      { produit: 'pa-r2', quantite: 1 },
    ],
  },
  {
    demoKey: 'ca-p3',
    name: 'Primo-vaccination chaton',
    description: 'Consultation, typhus-coryza, leucose et un comprimé vermifuge. Rappel trois semaines plus tard.',
    lignes: [
      { produit: 'pa-r10', quantite: 1 },
      { produit: 'pa-r3', quantite: 1 },
      { produit: 'ca-v8', quantite: 1 },
      { produit: 'pa-r6', quantite: 1 },
    ],
  },
  {
    demoKey: 'ca-p4',
    name: 'Castration chat',
    description: 'Acte, sédation, analgésie et anti-inflammatoire.',
    lignes: [
      { produit: 'ca-a3', quantite: 1 },
      { produit: 'ca-m12', quantite: 1 },
      { produit: 'ca-m13', quantite: 1 },
      { produit: 'ca-m11', quantite: 1 },
    ],
  },
  {
    demoKey: 'ca-p5',
    name: 'Rappel annuel cheval',
    description: 'Visite à l’écurie, tétanos, grippe équine et vermifuge.',
    lignes: [
      { produit: 'ca-a10', quantite: 1 },
      { produit: 'pa-r5', quantite: 1 },
      { produit: 'ca-v9', quantite: 1 },
      { produit: 'pa-r7', quantite: 1 },
    ],
  },
];

interface DemoPosologie {
  demoKey: string;
  libelle: string;
  /** Le calcul, par sa `demoKey` (`DEMO_CALCULS`). */
  calcul: string;
  /** Le produit, par sa `demoKey`. */
  produit: string;
  indication: string;
  especes: readonly string[];
  profil: 'Standard' | 'Senior' | 'Fragile';
  dose: number;
  doseMax?: number;
  doseUnit: 'mg/kg' | 'µg/kg';
  route?: string;
  note?: string;
  source: string;
}

const BRISTOL = 'Université de Bristol, fiche CSL_A07 (cas d’école), à valider par la clinique';
const IMPROVE = 'Improve International, anesthésie gériatrique (A. Katsiorchi, 2024), à valider par la clinique';

/** Les calculs que le vétérinaire choisit dans Mon poste : ils regroupent leurs posologies. */
export const DEMO_CALCULS: ReadonlyArray<{ demoKey: string; libelle: string; description: string; ageSenior: number }> = [
  { demoKey: 'ca-k1', libelle: 'Anesthésie', description: 'Prémédication (sédatif et opioïde), puis induction à effet.', ageSenior: 8 },
  { demoKey: 'ca-k2', libelle: 'Méthadone', description: 'Analgésie opioïde.', ageSenior: 8 },
];

/**
 * LES POSOLOGIES DE DEMONSTRATION. Profil Standard : les deux cas d'ecole de
 * l'Universite de Bristol (un chien de 20 kg, un chat de 5 kg). Profil Senior :
 * les doses reduites d'Improve International pour l'animal age. Aucune source
 * ne donne de dose « Fragile » (ASA III et plus) : le calculateur retombe alors
 * sur Senior, et le dit.
 */
export const DEMO_POSOLOGIES: ReadonlyArray<DemoPosologie> = [
  { demoKey: 'ca-d1', libelle: 'Acépromazine, prémédication chien', calcul: 'ca-k1', produit: 'ca-m7', indication: 'Prémédication', especes: ['Chien'], profil: 'Standard', dose: 0.04, doseUnit: 'mg/kg', route: 'IM', source: BRISTOL },
  { demoKey: 'ca-d2', libelle: 'Buprénorphine, prémédication chien', calcul: 'ca-k1', produit: 'ca-m8', indication: 'Prémédication', especes: ['Chien'], profil: 'Standard', dose: 20, doseUnit: 'µg/kg', route: 'IM', note: 'Se mélange à l’acépromazine dans la même seringue.', source: BRISTOL },
  { demoKey: 'ca-d3', libelle: 'Alfaxalone, induction chien', calcul: 'ca-k1', produit: 'ca-m9', indication: 'Induction', especes: ['Chien'], profil: 'Standard', dose: 2, doseUnit: 'mg/kg', route: 'IV', note: 'À effet : préparer une seringue pleine.', source: BRISTOL },
  { demoKey: 'ca-d5', libelle: 'Dexmédétomidine, prémédication chat', calcul: 'ca-k1', produit: 'ca-m12', indication: 'Prémédication', especes: ['Chat'], profil: 'Standard', dose: 2, doseUnit: 'µg/kg', route: 'IM', source: BRISTOL },
  { demoKey: 'ca-d6', libelle: 'Méthadone, prémédication chat', calcul: 'ca-k1', produit: 'ca-m13', indication: 'Prémédication', especes: ['Chat'], profil: 'Standard', dose: 0.1, doseUnit: 'mg/kg', route: 'IM', source: BRISTOL },
  { demoKey: 'ca-d7', libelle: 'Propofol, induction chat', calcul: 'ca-k1', produit: 'ca-m14', indication: 'Induction', especes: ['Chat'], profil: 'Standard', dose: 4, doseUnit: 'mg/kg', route: 'IV', note: 'À effet : préparer une seringue pleine.', source: BRISTOL },
  { demoKey: 'ca-d13', libelle: 'Acépromazine, prémédication chien âgé', calcul: 'ca-k1', produit: 'ca-m7', indication: 'Prémédication', especes: ['Chien'], profil: 'Senior', dose: 0.01, doseMax: 0.02, doseUnit: 'mg/kg', route: 'IM', note: 'Élimination plus lente chez l’animal âgé.', source: IMPROVE },
  { demoKey: 'ca-d14', libelle: 'Buprénorphine, prémédication chien âgé', calcul: 'ca-k1', produit: 'ca-m8', indication: 'Prémédication', especes: ['Chien'], profil: 'Senior', dose: 5, doseMax: 20, doseUnit: 'µg/kg', route: 'IV ou IM', source: IMPROVE },
  { demoKey: 'ca-d15', libelle: 'Alfaxalone, induction chien âgé', calcul: 'ca-k1', produit: 'ca-m9', indication: 'Induction', especes: ['Chien'], profil: 'Senior', dose: 0.5, doseUnit: 'mg/kg', route: 'IV', note: 'Dose de départ, lentement et à effet.', source: IMPROVE },
  { demoKey: 'ca-d16', libelle: 'Dexmédétomidine, prémédication chat âgé', calcul: 'ca-k1', produit: 'ca-m12', indication: 'Prémédication', especes: ['Chat'], profil: 'Senior', dose: 0.5, doseMax: 2, doseUnit: 'µg/kg', route: 'IV', source: IMPROVE },
  { demoKey: 'ca-d17', libelle: 'Propofol, induction chat âgé', calcul: 'ca-k1', produit: 'ca-m14', indication: 'Induction', especes: ['Chat'], profil: 'Senior', dose: 1, doseUnit: 'mg/kg', route: 'IV', note: 'Dose de départ, lentement et à effet.', source: IMPROVE },
  { demoKey: 'ca-d18', libelle: 'Méthadone, analgésie', calcul: 'ca-k2', produit: 'ca-m13', indication: 'Analgésie', especes: ['Chien', 'Chat'], profil: 'Standard', dose: 0.2, doseMax: 0.3, doseUnit: 'mg/kg', route: 'IV ou IM', note: 'Dose citée pour l’animal âgé : la clinique fixe sa dose adulte.', source: IMPROVE },
];

/** Les `demoKey` deja en base pour une liste de fiches, avec leur id. */
function idsParClef(fiches: ReadonlyArray<{ id: string; customFields?: Record<string, unknown> }>): Map<string, string> {
  const table = new Map<string, string>();
  for (const fiche of fiches) {
    const clef = fiche.customFields?.['demoKey'];
    if (typeof clef === 'string') table.set(clef, fiche.id);
  }
  return table;
}

/** Les trois champs de prevention d'un produit, avec les clefs de `taki.fields.json`. */
function champsDePrevention(prevention: NonNullable<DemoProduit['prevention']>): Record<string, string | string[]> {
  return { prevention: [...prevention.couvre], species: [...prevention.especes], reminderDelay: prevention.rappel };
}

/** Le nombre total de fiches que ce jeu pose, cite par le hub Parametres. */
export const DEMO_TOTAL =
  DEMO_PRODUITS.length +
  DEMO_PROTOCOLES.length +
  DEMO_PROTOCOLES.reduce((somme, protocole) => somme + protocole.lignes.length, 0) +
  DEMO_CALCULS.length +
  DEMO_POSOLOGIES.length;

const fiches = (n: number): string => `${n} ${n > 1 ? 'fiches' : 'fiche'}`;

/**
 * ECRIRE LE JEU DANS L'ORGANISATION COURANTE, et ne creer que ce qui manque.
 * L'ordre n'est pas libre : les articles, puis les protocoles, puis leurs
 * lignes (une ligne pointe son protocole et son article), puis les calculs, puis
 * les posologies (une posologie pointe son calcul et son produit).
 */
export async function importDemoDataset(sdk: TakiSdk, orgId: string): Promise<DemoImportResult> {
  let created = 0;
  let skipped = 0;

  /* ---- 1. Les articles (products::article) ---- */
  const articles = idsParClef(await sdk.products.list({ type: TYPE_ARTICLE, archived: 'all' }));
  for (const item of DEMO_PRODUITS) {
    if (articles.has(item.demoKey)) {
      skipped += 1;
      continue;
    }
    const produit = await sdk.products.create({
      orgId,
      type: TYPE_ARTICLE,
      name: item.name,
      sku: item.sku,
      unit: item.unit,
      basePrice: item.basePrice,
      taxRate: item.taxRate,
      customFields: {
        category: item.category,
        priceInclTax: ttc(item.basePrice, item.taxRate),
        ...(item.prevention === undefined ? {} : champsDePrevention(item.prevention)),
        ...(item.concentration === undefined ? {} : { concentration: item.concentration }),
        demoKey: item.demoKey,
      },
    });
    articles.set(item.demoKey, produit.id);
    created += 1;
  }

  /* ---- 2. Les protocoles (products::protocole), puis leurs lignes (compositions::ligne) ---- */
  const protocoles = idsParClef(await sdk.products.list({ type: TYPE_PROTOCOLE, archived: 'all' }));
  for (const item of DEMO_PROTOCOLES) {
    if (protocoles.has(item.demoKey)) {
      skipped += 1;
      continue;
    }
    const protocole = await sdk.products.create({
      orgId,
      type: TYPE_PROTOCOLE,
      name: item.name,
      customFields: { description: item.description, demoKey: item.demoKey },
    });
    protocoles.set(item.demoKey, protocole.id);
    created += 1;
  }
  const lignes = idsParClef(await sdk.compositions.list({ type: TYPE_LIGNE, archived: 'all' }));
  for (const protocole of DEMO_PROTOCOLES) {
    const protocoleId = protocoles.get(protocole.demoKey);
    for (const [rang, ligne] of protocole.lignes.entries()) {
      const demoKey = `${protocole.demoKey}-l${rang + 1}`;
      const articleId = articles.get(ligne.produit);
      if (lignes.has(demoKey) || protocoleId === undefined || articleId === undefined) {
        skipped += 1;
        continue;
      }
      await sdk.compositions.create({
        orgId,
        type: TYPE_LIGNE,
        productId: protocoleId,
        customFields: { articleId, quantite: ligne.quantite, demoKey },
      });
      created += 1;
    }
  }

  /* ---- 3. Les calculs de dose, puis les posologies (policies::calcul, policies::posologie) ---- */
  const calculs = idsParClef(await sdk.policies.list({ type: 'calcul', archived: 'all' }));
  for (const item of DEMO_CALCULS) {
    if (calculs.has(item.demoKey)) {
      skipped += 1;
      continue;
    }
    const calcul = await sdk.policies.create({
      orgId,
      type: 'calcul',
      customFields: { libelle: item.libelle, description: item.description, ageSenior: item.ageSenior, demoKey: item.demoKey },
    });
    calculs.set(item.demoKey, calcul.id);
    created += 1;
  }
  const posologies = idsParClef(await sdk.policies.list({ type: 'posologie', archived: 'all' }));
  for (const item of DEMO_POSOLOGIES) {
    const productId = articles.get(item.produit);
    const calculId = calculs.get(item.calcul);
    if (posologies.has(item.demoKey) || productId === undefined || calculId === undefined) {
      skipped += 1;
      continue;
    }
    await sdk.policies.create({
      orgId,
      type: 'posologie',
      customFields: {
        libelle: item.libelle,
        calculId,
        productId,
        indication: item.indication,
        species: [...item.especes],
        profil: item.profil,
        dose: item.dose,
        ...(item.doseMax === undefined ? {} : { doseMax: item.doseMax }),
        doseUnit: item.doseUnit,
        ...(item.route === undefined ? {} : { route: item.route }),
        ...(item.note === undefined ? {} : { note: item.note }),
        source: item.source,
        demoKey: item.demoKey,
      },
    });
    created += 1;
  }

  const dejaLa = skipped === 0 ? "rien n'existait déjà" : `${skipped} déjà ${skipped > 1 ? 'présentes' : 'présente'}`;
  const message =
    created === 0
      ? `Le jeu de démonstration est déjà en place (${fiches(skipped)}).`
      : `${fiches(created)} ${created > 1 ? 'créées' : 'créée'}, ${dejaLa}.`;
  return { created, skipped, message };
}
